/**
 * Логотип ANROYAL.
 *
 * Знак — глобус с литерой «A» и росчерком, как в макете приложения.
 * Построен геометрией: меридианы и параллели считаются по широте, поэтому
 * шар выглядит шаром, а не набором случайных дуг.
 *
 * Обвести знак с макета не вышло: там он нарисован объёмным металлом
 * с градиентами, и любой порог по яркости рвёт его — либо пропадает низ,
 * либо рассыпаются тонкие линии глобуса. Исходник в вектор не присылали.
 *
 * Надписи, наоборот, обведены с присланного изображения логотипа: они
 * плоские и светлые, и контур снялся чисто.
 *
 * Всё залито currentColor, поэтому знак перекрашивается вместе с темой.
 */
import styles from './Logo.module.css';

type Props = { size?: number; className?: string | undefined };

/** Знак: глобус, литера «A» и росчерк вокруг. */
export function Emblem({ size = 42, className }: Props) {
  return (
    <svg
      width={size}
      height={size * 0.9}
      viewBox="0 0 200 180"
      fill="none"
      className={className}
      aria-hidden
      focusable="false"
    >
      <g fill="none" stroke="currentColor" strokeWidth="3.1" strokeLinecap="round">
        <circle cx="95" cy="84" r="70" />
        <ellipse cx="95" cy="84" rx="44" ry="70" />
        <ellipse cx="95" cy="84" rx="21" ry="70" />
        <line x1="95" y1="14" x2="95" y2="154" />
        <line x1="25" y1="84" x2="165" y2="84" />
        <ellipse cx="95" cy="44.9" rx="58.0" ry="15.1" />
        <ellipse cx="95" cy="123.1" rx="58.0" ry="15.1" />
      </g>

      {/* Литера без перекладины — её роль играет росчерк. */}
      <path d="M95 18 L45 162 H72 L95 92 L118 162 H145 Z" fill="currentColor" />

      {/* Росчерк: наклонное кольцо вокруг знака. */}
      <ellipse
        cx="95"
        cy="115"
        rx="84"
        ry="25"
        transform="rotate(-15 95 115)"
        fill="none"
        stroke="currentColor"
        strokeWidth="7.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Надпись ANROYAL. */
function Wordmark({ className }: { className?: string | undefined }) {
  return (
    <svg viewBox="0 0 1160 210" fill="none" className={className}
      aria-hidden focusable="false">
      <g transform="translate(0.000000,210.000000) scale(0.100000,-0.100000)" fill="currentColor" stroke="none">
        <path d="M6210 1584 c-200 -36 -330 -106 -442 -238 -87 -104 -149 -265 -151
-396 -2 -122 74 -334 151 -423 23 -26 45 -53 49 -61 18 -30 138 -106 215 -136
326 -125 717 17 855 310 41 87 73 224 73 312 0 66 -34 189 -77 276 -48 97
-166 220 -265 275 -102 57 -310 98 -408 81z m198 -238 c116 -31 205 -107 261
-225 36 -75 36 -77 34 -193 -2 -105 -5 -124 -32 -187 -45 -105 -91 -155 -188
-203 -80 -40 -86 -41 -185 -42 -172 -2 -296 62 -371 190 -36 61 -67 167 -67
231 0 93 74 276 136 334 97 92 271 132 412 95z M968 1559 c-25 -14 -96 -148
-274 -514 -32 -66 -89 -175 -126 -243 -37 -67 -68 -125 -68 -128 0 -11 -118
-242 -144 -281 -43 -64 -35 -77 54 -85 80 -8 137 -3 158 14 7 6 29 45 49 87
20 42 45 87 55 100 10 13 18 32 18 41 0 21 22 41 47 42 10 0 158 -1 330 -1
369 -2 338 9 417 -150 70 -139 69 -138 188 -139 123 0 133 8 89 75 -17 25 -31
54 -31 62 0 9 -6 25 -14 36 -12 18 -133 249 -276 530 -30 61 -74 146 -97 190
-23 44 -71 142 -107 217 -36 75 -71 140 -77 144 -19 11 -172 14 -191 3z m124
-341 c8 -13 24 -45 35 -73 12 -27 51 -104 87 -169 36 -66 66 -127 66 -136 0
-36 -23 -40 -230 -43 -184 -2 -204 0 -214 16 -9 13 -6 25 10 55 12 20 54 102
94 182 73 145 103 190 126 190 6 0 18 -10 26 -22z M4085 1558 c-11 -6 -24 -16
-28 -22 -9 -13 -1 -1212 8 -1226 8 -13 171 -13 195 0 16 9 18 27 19 209 1 110
4 207 7 215 9 25 42 30 173 27 152 -3 151 -3 240 -135 235 -352 208 -326 342
-326 75 0 101 4 113 16 21 20 23 16 -34 79 -27 30 -50 60 -50 65 0 5 -21 41
-47 81 -27 39 -57 86 -69 105 -11 19 -33 47 -48 63 -37 39 -28 62 36 92 176
82 255 249 214 448 -19 88 -59 155 -130 215 -73 62 -108 77 -215 92 -120 17
-696 18 -726 2z m734 -222 c78 -41 123 -135 102 -214 -22 -85 -42 -116 -86
-130 -59 -20 -262 -34 -399 -28 -165 7 -159 -1 -157 202 2 169 2 171 35 185
35 15 471 2 505 -15z M7267 1559 c-25 -15 -21 -59 9 -90 31 -32 46 -54 238
-341 83 -123 167 -245 187 -273 53 -71 59 -105 59 -326 0 -233 -4 -224 107
-223 109 1 105 -9 110 251 5 215 6 219 34 275 16 31 33 59 39 63 5 3 42 54 82
112 40 58 94 136 120 172 26 36 72 104 104 151 31 47 76 108 100 137 73 85 60
101 -82 98 -112 -1 -142 -13 -169 -64 -12 -25 -58 -93 -191 -289 -12 -18 -34
-47 -49 -65 -14 -17 -36 -48 -49 -67 -41 -65 -58 -57 -144 70 -29 41 -74 107
-102 145 -27 39 -59 88 -71 110 -26 48 -94 141 -110 151 -19 11 -202 14 -222
3z M2272 1548 c-9 -9 -12 -160 -12 -620 0 -590 1 -608 19 -618 27 -14 194 -13
208 1 9 9 10 117 6 403 -4 331 -3 394 9 410 17 22 50 10 68 -24 6 -11 38 -53
73 -93 34 -41 87 -105 117 -142 30 -37 79 -92 109 -123 30 -32 85 -93 122
-137 38 -44 84 -98 103 -120 18 -22 55 -68 81 -103 26 -34 55 -67 65 -72 27
-15 143 -12 164 3 20 15 17 -61 30 885 5 387 13 362 -117 362 -128 0 -119 34
-115 -424 2 -212 0 -392 -4 -401 -15 -26 -35 -17 -72 33 -71 94 -82 108 -136
173 -102 123 -309 368 -357 424 -36 40 -82 104 -98 134 -24 47 -61 61 -163 61
-55 0 -92 -4 -100 -12z M9200 1540 c-11 -11 -20 -27 -20 -35 0 -9 -58 -134
-130 -280 -71 -145 -130 -266 -130 -270 0 -3 -26 -61 -59 -128 -89 -186 -127
-267 -150 -327 -22 -54 -38 -88 -70 -143 -24 -42 2 -58 99 -56 100 1 127 12
141 58 11 38 55 120 101 188 l27 40 296 1 c321 0 344 -2 362 -39 43 -88 57
-119 75 -166 27 -74 44 -83 153 -83 91 0 115 8 115 39 0 8 -32 80 -71 160 -39
80 -86 178 -104 216 -61 131 -66 140 -80 165 -8 14 -51 102 -96 195 -44 94
-100 211 -124 260 -23 50 -50 111 -60 137 -26 75 -50 88 -161 88 -81 0 -97 -3
-114 -20z m166 -347 c14 -32 36 -80 49 -108 13 -27 38 -84 56 -125 17 -41 40
-84 50 -96 21 -23 25 -59 7 -70 -6 -4 -99 -7 -206 -6 -240 1 -247 4 -193 92 5
8 21 42 36 75 15 33 43 92 61 130 19 39 34 76 34 84 0 7 9 29 21 47 32 52 55
45 85 -23z M10480 1540 c-23 -23 -25 -98 -13 -795 6 -367 8 -410 24 -426 15
-15 54 -17 431 -18 262 -1 424 2 441 8 27 10 27 12 27 100 0 49 -4 92 -9 96
-6 3 -155 7 -332 9 -264 3 -324 7 -336 19 -12 12 -14 88 -13 502 1 574 12 525
-117 525 -70 0 -86 -3 -103 -20z" />
      </g>
    </svg>
  );
}

/** Подпись VPN. */
function SubMark({ className }: { className?: string | undefined }) {
  return (
    <svg viewBox="0 0 450 160" fill="none" className={className}
      aria-hidden focusable="false">
      <g transform="translate(0.000000,160.000000) scale(0.100000,-0.100000)" fill="currentColor" stroke="none">
        <path d="M496 1079 c-16 -24 -5 -61 28 -88 8 -7 18 -27 21 -44 4 -18 14 -43
22 -55 13 -21 43 -91 155 -357 43 -103 98 -213 113 -225 24 -20 78 -8 92 20
50 105 132 281 143 310 7 19 22 47 32 62 10 14 18 30 18 34 0 4 21 53 46 108
25 56 57 127 72 159 31 68 24 90 -29 95 -47 4 -66 -13 -93 -88 -13 -36 -56
-141 -96 -235 -39 -93 -78 -192 -85 -219 -8 -27 -18 -53 -24 -59 -18 -18 -58
1 -71 35 -31 78 -92 225 -105 253 -7 17 -29 66 -48 110 -18 44 -42 99 -52 123
-33 77 -105 108 -139 61z M1986 1077 c-13 -18 -15 -80 -15 -368 -1 -191 -3
-354 -7 -362 -8 -21 30 -57 61 -57 53 0 63 26 58 153 -5 152 -11 147 192 147
142 0 154 2 203 26 96 48 132 111 132 232 0 105 -47 185 -133 226 -50 24 -60
25 -264 25 -210 1 -213 1 -227 -22z m410 -93 c54 -8 104 -78 104 -146 0 -38
-36 -103 -69 -126 -37 -25 -310 -32 -330 -8 -14 17 -27 211 -17 252 3 12 18
27 33 33 26 9 206 6 279 -5z M3321 1083 c-21 -18 -22 -22 -22 -383 0 -201 0
-370 1 -376 0 -16 45 -34 68 -27 31 8 33 20 38 296 2 143 6 263 10 268 12 20
35 7 68 -38 62 -85 110 -144 163 -203 28 -32 64 -77 79 -99 14 -23 30 -41 33
-41 4 0 15 -15 26 -32 10 -18 38 -59 62 -90 39 -51 49 -58 78 -58 59 0 59 2
57 504 -2 270 -6 296 -56 296 -48 0 -51 -14 -51 -294 0 -259 0 -261 -21 -264
-17 -2 -39 19 -100 95 -106 131 -134 170 -164 219 -65 109 -190 244 -226 244
-12 0 -31 -8 -43 -17z" />
      </g>
    </svg>
  );
}

/** Знак вместе с надписями — то, что стоит в шапке. */
export function Logo({ size = 44 }: { size?: number }) {
  return (
    <span className={styles.logo}>
      <Emblem size={size} className={styles.silver} />
      <span className={styles.words}>
        <Wordmark className={styles.name} />
        <SubMark className={styles.sub} />
      </span>
    </span>
  );
}
